import axios from 'axios';

import { apiConst, keyConst, twoFactorConst } from '@/constants';

import { clearAuthenticatedSession, persistAuthenticatedSession } from '@/utils/authSession';
import { isHmacRequestFailure } from '@/utils/hmac';
import { notifyAccessTokenUpdated, shouldAttemptRefresh } from '@/utils/tokenRefresh';

import { Sentry, clearSentryUserContext, sanitizeUrl } from '@/sentry';

const shouldCaptureApiError = (error: unknown) => {
  if (!axios.isAxiosError(error)) return true;
  const status = error.response?.status;
  return !status || status >= 500;
};

export const captureApiException = (error: unknown, action: string, extra?: Record<string, unknown>) => {
  if (!shouldCaptureApiError(error)) return;

  if (axios.isAxiosError(error)) {
    const method = error.config?.method?.toUpperCase();
    const statusCode = error.response?.status;
    const message = statusCode ? `API request failed with ${statusCode}` : error.message || 'API request failed';

    Sentry.captureException(new Error(message), {
      tags: {
        module: 'frontend-api',
        action,
        'http.method': method || 'UNKNOWN',
        'http.status_code': statusCode ? String(statusCode) : 'network',
      },
      extra: {
        url: sanitizeUrl(error.config?.url),
        method,
        statusCode,
        statusText: error.response?.statusText,
        message: error.message,
        ...extra,
      },
    });
    return;
  }

  Sentry.captureException(error, {
    tags: { module: 'frontend-api', action },
    extra,
  });
};

export const requester = axios.create({
  baseURL: import.meta.env.VITE_BASE_URL || 'http://localhost:2023',
  withCredentials: true,
});

let refreshInFlight: Promise<string | null> | null = null;

const clearClientSession = (): void => {
  clearAuthenticatedSession();
  clearSentryUserContext();
};

const refreshAccessToken = async (): Promise<string | null> => {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const { refreshAuthToken } = await import('@/api/auth');
      const response = await refreshAuthToken();
      const nextToken = response.data?.access_token;
      if (!nextToken) return null;
      persistAuthenticatedSession(nextToken);
      notifyAccessTokenUpdated(nextToken);
      return nextToken;
    })().finally(() => {
      refreshInFlight = null;
    });
  }

  return refreshInFlight;
};

requester.interceptors.request.use(
  (config) => {
    const requestUrl = String(config.url || '');
    const token = localStorage.getItem(keyConst.userToken);
    if (token && !requestUrl.includes(apiConst.authRefresh)) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    captureApiException(error, 'request');
    return Promise.reject(error);
  },
);

requester.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (isHmacRequestFailure(error)) {
      captureApiException(error, 'hmac');
      return Promise.reject(error);
    }

    const originalRequest = error.config;
    const currentPath = window.location.pathname;
    const authRoutes = ['/auth/signin', '/auth/signup', twoFactorConst.verifyPath, twoFactorConst.setupPath];
    const noRedirect401Endpoints = [apiConst.authTwoFactorSetupVerify, apiConst.authTwoFactorLoginVerify];
    const requestUrl = String(originalRequest?.url || '');
    const isRefreshRequest = requestUrl.includes(apiConst.authRefresh);
    const shouldSkipAuthRedirect =
      authRoutes.includes(currentPath) || noRedirect401Endpoints.some((endpoint) => requestUrl.includes(endpoint));

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry && !isRefreshRequest) {
      originalRequest._retry = true;

      if (shouldAttemptRefresh()) {
        try {
          const nextToken = await refreshAccessToken();
          if (nextToken) {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${nextToken}`;
            return requester(originalRequest);
          }
        } catch (refreshError) {
          if (isHmacRequestFailure(refreshError)) {
            captureApiException(refreshError, 'hmac.refresh');
            return Promise.reject(refreshError);
          }
          captureApiException(refreshError, 'token-refresh');
        }
      }

      clearClientSession();
      if (!shouldSkipAuthRedirect) window.location.href = '/auth/signin';
      return Promise.reject(error);
    }

    captureApiException(error, 'response');
    return Promise.reject(error);
  },
);
